import {
  Avatar,
  CircularProgress,
  TextareaAutosize,
  Tooltip,
} from '@mui/material';
import { LinkedinStatusBadgeWrapper } from 'components/LinkedinStatusBadge/styles';
import { useAccountDrawer } from 'context/accountDrawer';
import LinkedinIcon from 'crono-fe-common/icons/Icon-Linkedin';
import IconTimeZone from 'crono-fe-common/icons/Icon-Time-Zone';
import ClockIconTwelveFifteen from 'crono-fe-common/icons/Icon-Clock-12-15';
import { colors } from 'crono-fe-common/theme';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import {
  adjustDateToTimezone,
  getProspectName,
  transformToValidUrl,
} from 'crono-fe-common/utils';
import useGetProspect from 'hooks/services/prospect/useGetProspect';
import { format, isSameDay, formatDistanceToNow } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { stringAvatarProspect } from 'utils/formatUtils';
import { Button } from '@crono-one/design-system';
import { useLogGeneric } from 'hooks/services/event/useLogGeneric';
import { LogGeneric } from 'crono-fe-common/types/logGeneric';
import { useAnalytics } from 'context/analytics';
import { cn } from 'utils/style-utils';
import { useHideTaskConstruction } from '../hideTaskConstructionContext';
import { useAuth } from 'context/auth';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { OpportunityBadge } from 'components/OpportunityBadge';
import { ProspectLeadScoreBadge } from 'pages/leadScore/components/ProspectLeadScoreBadge';
import { useLeadScoreAccess } from 'pages/leadScore/hooks/useLeadScoreAccess';
import { TaskSubjectDisplay } from 'pages/accountDrawer/TaskSubjectDisplay';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import TaskExecutionFooterActions from 'pages/accountDrawer/TaskExecutionFooterActions';
import { genericDescriptionToText } from './genericView.utils';

interface IGenericViewProps {
  task?: TaskTodo | null;
  activity?: {
    description?: string | null;
    subject?: string | null;
    createdDate: string | null;
    user?: string | null;
  } | null;
  selectedProspectId?: string;
  callOnSuccess?: () => void;
  handleCloseModal?: () => void;
  close?: () => void;
}

export const GenericView = ({
  selectedProspectId,
  activity,
  callOnSuccess,
  task,
}: IGenericViewProps) => {
  const { account } = useAccountDrawer();
  const { hasOpportunities } = useAuth();
  const { data: prospectFromDb } = useGetProspect(
    selectedProspectId,
    true,
    true,
    true,
    true,
  );
  const analytics = useAnalytics();
  const { setHideProgressTask } = useHideTaskConstruction();

  const isLogView = !!activity;

  const initialSubject = task?.subject ?? activity?.subject ?? '';
  const initialDescription = genericDescriptionToText(
    task?.description ?? activity?.description,
  );

  const [description, setDescription] = useState<string>(initialDescription);

  useEffect(() => {
    setDescription(initialDescription);
  }, [initialDescription, task, activity]); // We add task and activity to the dependencies to always refresh when they change, even if initialDescription is null between them and user put something in description in the first place

  const prospect = prospectFromDb?.data?.data ?? null;
  const prospectName = getProspectName(prospect);

  const { shouldShowScoreForProspect } = useLeadScoreAccess();
  const shouldShowScore = shouldShowScoreForProspect(prospect);

  const currentDate = useMemo(() => {
    if (!prospect?.timeZone) return;
    return adjustDateToTimezone(new Date(), prospect.timeZone);
  }, [prospect]);

  const { mutate: createLogGeneric, isLoading: isLoggingGeneric } =
    useLogGeneric();

  const handleLogGeneric = () => {
    if (account && (prospect || task?.type === TaskTodoType.GENERIC)) {
      const logGeneric: LogGeneric = {
        accountId: account.objectId,
        prospectId: prospect?.objectId ?? null,
        taskTodoId: task?.id ?? null,
        opportunityId: task?.opportunityId ?? null,
        subject: task?.subject ?? null,
        description,
      };

      if (task?.id) {
        analytics?.track('sequence-task', {});
      }

      analytics?.track('log-generic', {});

      createLogGeneric(
        withMeta(
          { ...logGeneric },
          {
            successMessage: 'To-do logged successfully',
            errorMessage: 'Error logging todo',
          },
        ),
        {
          onSuccess: callOnSuccess,
        },
      );
    }
  };

  useEffect(() => {
    if (prospect || !selectedProspectId) {
      setHideProgressTask(false);
    }
  }, [prospect, selectedProspectId, task]);

  return (
    <div className="h-full flex flex-col gap-6 p-6 overflow-hidden">
      <div className="flex justify-between w-full gap-4">
        <div className="flex-1 flex items-center overflow-hidden">
          {prospect && (
            <Avatar
              key={prospect.objectId}
              className="mr-4 size-4 text-base bg-primary-100 text-primary-700"
            >
              {stringAvatarProspect(prospect)}
            </Avatar>
          )}
          <div className="flex flex-col overflow-hidden justify-center w-full">
            <div className="flex items-center gap-1 w-full justify-start">
              <Tooltip
                title={prospect ? null : 'No assigned contact'}
                enterDelay={400}
                enterNextDelay={400}
                arrow
              >
                <div
                  className={cn(
                    'text-sm font-medium leading-5 truncate',
                    prospect ? 'text-gray-900' : 'text-neutral-500',
                  )}
                >
                  {prospectName ? prospectName : 'No contact'}
                  {prospect?.linkedinStatus === 'Connected' && (
                    <LinkedinStatusBadgeWrapper>
                      • 1st
                    </LinkedinStatusBadgeWrapper>
                  )}
                </div>
              </Tooltip>
              {prospect && (
                <Tooltip
                  title={
                    !prospect?.linkedin ? 'No Linkedin profile URL found' : ''
                  }
                  placement="top"
                  arrow
                >
                  <div className="flex items-center">
                    <a
                      href={
                        transformToValidUrl(prospect?.linkedin) ?? undefined
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'size-5 p-0.5 cursor-pointer flex items-center',
                        {
                          'pointer-events-none': !prospect?.linkedin,
                        },
                      )}
                      onClick={() => {
                        if (!prospect?.linkedin) return;
                        const url = transformToValidUrl(prospect?.linkedin);
                        if (url) window.open(url, '_blank');
                      }}
                    >
                      <LinkedinIcon
                        color={!prospect?.linkedin ? colors.grey3 : colors.blue}
                      />
                    </a>
                  </div>
                </Tooltip>
              )}
              {shouldShowScore && (
                <div>
                  <ProspectLeadScoreBadge prospect={prospect} size="sm" />
                </div>
              )}
              <OpportunityBadge
                opportunityId={task?.opportunityId}
                opportunityName={task?.opportunity?.name}
                hasOpportunities={hasOpportunities}
              />
            </div>

            <div className="flex items-center gap-1 w-full justify-start">
              <div className="text-xs leading-4 text-gray-700">
                {prospect?.title}
              </div>
              {prospect?.timeZone && (
                <>
                  <div className="small-divider" />
                  <IconTimeZone className="timezone-icon" />
                  <div className="text-xs leading-4 text-gray-700">
                    {currentDate?.slice(11, 16)} local time
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <TaskSubjectDisplay task={task} prospect={prospect} />
      </div>

      <div className="flex flex-col gap-4 flex-1 h-full overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="text-base font-semibold text-gray-900">
            {initialSubject}
          </div>

          {!isLogView ? (
            <div className="text-3xs font-medium bg-gray-200 rounded-lg p-1 text-gray-700 flex items-center gap-1">
              <ClockIconTwelveFifteen color="currentColor" className="size-3" />
              {task?.activityDate
                ? isSameDay(new Date(task.activityDate), new Date())
                  ? format(new Date(task.activityDate), 'HH:mm')
                  : formatDistanceToNow(new Date(task.activityDate), {
                      addSuffix: true,
                    })
                : '—'}
            </div>
          ) : (
            <div className="flex flex-1 items-center gap-2 justify-between">
              <div className="text-sm bg-gray-200 rounded-lg p-1 text-neutral-800 font-medium">
                {activity?.user}
              </div>

              <div className="text-sm flex gap-1">
                <span className="font-medium text-gray-700">Executed on:</span>
                {activity.createdDate &&
                  format(new Date(activity.createdDate), 'MMM d, yyyy')}
                ・
                {activity.createdDate &&
                  format(new Date(activity.createdDate), 'HH:mm')}
              </div>
            </div>
          )}
        </div>

        <TextareaAutosize
          className="overflow-auto!"
          style={{
            fontFamily: 'Poppins, sans-serif',
            flexGrow: 1,
            padding: '12px',
            borderRadius: '16px',
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '24px',
            color: colors.black,
            resize: 'none',
            backgroundColor: colors.grey6,
          }}
          value={description}
          disabled={isLogView}
          placeholder="Insert a description..."
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {!isLogView && (
        <div className="flex items-center justify-between flex-wrap gap-x-6 gap-y-4">
          {isLoggingGeneric ? (
            <div className="flex justify-center items-center h-10">
              <CircularProgress />
            </div>
          ) : (
            <Button onClick={handleLogGeneric}>Log Task</Button>
          )}
          {task?.id && <TaskExecutionFooterActions taskId={task.id} />}
        </div>
      )}
    </div>
  );
};
